import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import api from "../api";

export default function PaymentSuccess() {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user") || "null");
  const token = user?.access;

  useEffect(() => {
    const processPayment = async () => {
      const params = new URLSearchParams(window.location.search);

      // PayPal returns the PayPal order ID as "token".
      const paypalOrderID = params.get("token");

      // Stripe returns the Checkout Session ID as "session_id".
      const stripeSessionID = params.get("session_id");

      // =====================================================
      // STRIPE
      // =====================================================
      // Stripe payment is finalized by the Stripe webhook.
      // Do not try to capture it here. The webhook marks the
      // order as paid, marks the rug as sold, and removes the
      // purchased product from active carts.

      if (stripeSessionID) {
        setOrder({
          order_number: null,
          total_amount: null,
          status: "paid",
          stripe_session_id: stripeSessionID,
        });
        setLoading(false);
        return;
      }

      // =====================================================
      // PAYPAL
      // =====================================================

      if (!paypalOrderID) {
        setError("Payment information was not found.");
        setLoading(false);
        return;
      }

      if (!token) {
        setError("Please sign in to view your order.");
        setLoading(false);
        return;
      }

      try {
        const captureRes = await api.post(
          "/api/capture-paypal-order/",
          {
            orderID: paypalOrderID,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const captureData = captureRes.data;

        setOrder({
          id: captureData.order_id,
          order_number:
            captureData.order_number ||
            captureData.order_id,
          total_amount:
            captureData.paypal?.purchase_units?.[0]?.amount?.value ||
            captureData.total_amount ||
            null,
          status: captureData.status,
          paypal_order_id:
            captureData.paypal_order_id || paypalOrderID,
          paypal_capture_id:
            captureData.paypal_capture_id || null,
        });

        localStorage.removeItem("cart");
      } catch (err) {
        console.error("Payment capture error:", err);

        const message =
          err.response?.data?.detail ||
          err.response?.data?.error ||
          err.message ||
          "Something went wrong while processing your payment.";

        setError(message);
      } finally {
        setLoading(false);
      }
    };

    processPayment();
  }, [token]);

  // =====================================
  // LOADING
  // =====================================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F0E6] px-4">
        <div className="w-full max-w-md bg-[#FFFDF9] border border-[#E8DED2] rounded-2xl shadow-sm p-8 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-10 h-10 border-4 border-[#DDE8E5] border-t-[#46736C] rounded-full animate-spin" />
          </div>

          <h1 className="text-xl font-semibold text-[#332A24]">
            Processing your payment
          </h1>

          <p className="text-sm text-[#76695D] mt-2">
            Please wait while we confirm your payment.
          </p>
        </div>
      </div>
    );
  }

  // =====================================
  // ERROR
  // =====================================

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F0E6] px-4">
        <div className="w-full max-w-md bg-[#FFFDF9] border border-[#E8DED2] rounded-2xl shadow-sm p-8">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-50 flex items-center justify-center">
              <span className="text-2xl text-red-500">×</span>
            </div>

            <h1 className="text-2xl font-semibold text-[#332A24]">
              Payment could not be confirmed
            </h1>

            <p className="text-sm text-[#76695D] mt-3">
              {error}
            </p>
          </div>

          <div className="mt-8 space-y-3">
            <button
              onClick={() => navigate("/orders")}
              className="w-full bg-[#46736C] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#385F59] transition"
            >
              View orders
            </button>

            <button
              onClick={() => navigate("/")}
              className="w-full text-sm text-[#76695D] hover:text-[#46736C] transition"
            >
              Return to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =====================================
  // SUCCESS
  // =====================================

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F0E6] px-4">
      <div className="w-full max-w-md bg-[#FFFDF9] border border-[#E8DED2] rounded-2xl shadow-sm p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#E2F0ED] flex items-center justify-center">
            <span className="text-2xl text-[#46736C]">✓</span>
          </div>

          <h1 className="text-2xl font-semibold text-[#332A24]">
            Payment successful
          </h1>

          <p className="text-sm text-[#76695D] mt-2">
            Your order has been placed successfully.
          </p>
        </div>

        {order && (
          <div className="space-y-4 border-t border-[#E8DED2] pt-6">
            {order.order_number && (
              <div className="flex justify-between">
                <span className="text-sm text-[#76695D]">
                  Order ID
                </span>

                <span className="text-sm font-medium text-[#332A24]">
                  #{order.order_number}
                </span>
              </div>
            )}

            {order.total_amount && (
              <div className="flex justify-between">
                <span className="text-sm text-[#76695D]">
                  Total
                </span>

                <span className="text-sm font-semibold text-[#46736C]">
                  ${Number(order.total_amount).toFixed(2)}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-sm text-[#76695D]">
                Status
              </span>

              <span className="text-sm font-semibold text-[#46736C] capitalize">
                {order.status}
              </span>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-3">
          <button
            onClick={() => navigate("/orders")}
            className="w-full bg-[#46736C] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#385F59] transition"
          >
            View orders
          </button>

          <button
            onClick={() => navigate("/")}
            className="w-full text-sm text-[#76695D] hover:text-[#46736C] transition"
          >
            Return to home
          </button>
        </div>
      </div>
    </div>
  );
}
